import { useState } from "react";
import confetti from "canvas-confetti";
import {
  Tv, Ghost, Pizza, ShoppingBag, CreditCard, Music, Cloud, Dumbbell,
  ShieldCheck, Wifi, Zap, Gamepad2, Newspaper,
  Sword, CheckCircle2, MoreVertical, Pencil, Trash2, RotateCcw, Skull,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import api from "@/lib/api";
import { toast } from "sonner";
import AddSubscriptionDialog from "@/components/AddSubscriptionDialog";
import AppStoreTriageBubble from "@/components/AppStoreTriageBubble";
import useDevicePlatform, { detectAppStore } from "@/lib/useDevicePlatform";

const ICONS = {
  Tv, Ghost, Pizza, ShoppingBag, CreditCard, Music, Cloud, Dumbbell,
  ShieldCheck, Wifi, Zap, Gamepad2, Newspaper,
};

const COLOR_MAP = {
  midnight: { bg: "bg-slate-900",  text: "text-white",      ring: "ring-slate-900/10", chip: "bg-slate-100 text-slate-700" },
  gold:     { bg: "bg-amber-400",  text: "text-slate-900",  ring: "ring-amber-400/20", chip: "bg-amber-100 text-amber-800" },
  amber:    { bg: "bg-amber-400",  text: "text-slate-900",  ring: "ring-amber-400/20", chip: "bg-amber-100 text-amber-800" },
  red:      { bg: "bg-red-500",    text: "text-white",      ring: "ring-red-500/20",   chip: "bg-red-100 text-red-700" },
  emerald:  { bg: "bg-emerald-500",text: "text-white",      ring: "ring-emerald-500/20",chip: "bg-emerald-100 text-emerald-800" },
};

function fireConfetti(originX = 0.5) {
  const base = { spread: 70, ticks: 90, gravity: 1, decay: 0.92, startVelocity: 35, scalar: 0.9 };
  confetti({ ...base, particleCount: 70, origin: { x: originX, y: 0.7 }, colors: ["#F59E0B", "#FBBF24", "#0F172A", "#EF4444", "#10B981"] });
  confetti({ ...base, particleCount: 40, angle: 60,  origin: { x: 0, y: 0.8 } });
  confetti({ ...base, particleCount: 40, angle: 120, origin: { x: 1, y: 0.8 } });
}

export const BattleCard = ({ sub, onDefeat, onChanged, onDeleted, index = 0 }) => {
  const [defeating, setDefeating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const platform = useDevicePlatform();
  // Prefer the backend-computed vendor_hint (derived from Plaid's raw
  // descriptor, e.g. "APL*PROMINENT.IO"), fall back to name-based detection
  // for legacy subs that were created before vendor_hint existed.
  const triageVendor = sub.vendor_hint || detectAppStore(sub.name);
  const dismissKey = `mmh_triage_dismissed_${sub.id}`;
  const [triageHidden, setTriageHidden] = useState(() => {
    if (typeof window === "undefined") return false;
    try { return window.localStorage.getItem(dismissKey) === "true"; }
    catch { return false; }
  });
  const handleTriageDismiss = () => {
    setTriageHidden(true);
    try { window.localStorage.setItem(dismissKey, "true"); } catch { /* ignore */ }
  };
  const Icon = ICONS[sub.icon] || CreditCard;
  const palette = COLOR_MAP[sub.color] || COLOR_MAP.midnight;

  const cycle = sub.cycle_days || 30;
  const remaining = Math.max(0, Math.min(cycle, sub.days_until_billing ?? 14));
  const progressPct = Math.round(((cycle - remaining) / cycle) * 100);
  const urgent = remaining <= 5;
  const isDefeated = !!sub.defeated;

  const handleDefeat = (event) => {
    if (defeating) return;
    setDefeating(true);
    const rect = event?.currentTarget?.getBoundingClientRect?.();
    const originX = rect ? Math.max(0.05, Math.min(0.95, (rect.left + rect.width / 2) / window.innerWidth)) : 0.5;
    fireConfetti(originX);
    window.setTimeout(() => onDefeat?.(sub.id, sub), 450);
  };

  const handleRestore = async () => {
    if (restoring) return;
    setRestoring(true);
    try {
      const { data } = await api.patch(`/subscriptions/${sub.id}`, { defeated: false });
      toast.success(`${sub.name} restored.`, { description: "Back in your active battles." });
      onChanged?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not restore.");
    } finally {
      setRestoring(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${sub.name}"? This can't be undone.`)) return;
    try {
      await api.delete(`/subscriptions/${sub.id}`);
      toast.success(`${sub.name} removed.`);
      onDeleted?.(sub.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete.");
    }
  };

  return (
    <div
      className={`mmh-fade-up ${defeating ? "mmh-defeat-out" : ""} relative bg-white rounded-3xl border-2 shadow-sm hover:shadow-md transition-all duration-200 p-5 overflow-hidden ${
        isDefeated ? "border-slate-200 bg-slate-50 grayscale opacity-70" : "border-slate-100"
      }`}
      style={{ animationDelay: `${index * 120}ms` }}
      data-testid={`battle-card-${sub.id}`}
      data-defeated={isDefeated ? "true" : "false"}
      data-triage-vendor={triageVendor || ""}
    >
      {isDefeated && (
        <div
          className="pointer-events-none absolute top-4 right-4 select-none"
          aria-hidden="true"
          data-testid={`battle-card-slayed-stamp-${sub.id}`}
        >
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md border-2 border-red-700/80 text-red-700 font-black text-[11px] tracking-[0.2em] uppercase rotate-[-8deg] shadow-sm bg-white/90"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            <Skull className="w-3.5 h-3.5" strokeWidth={2.6} /> Slayed
          </span>
        </div>
      )}
      {triageVendor && !isDefeated && !triageHidden && (
        <AppStoreTriageBubble
          vendor={triageVendor}
          platform={platform}
          subName={sub.name}
          onDismiss={handleTriageDismiss}
        />
      )}
      <div className="flex items-start gap-4">
        <div
          className={`flex-shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center ring-4 ${
            isDefeated
              ? "bg-slate-400 text-white ring-slate-400/20"
              : `${palette.bg} ${palette.text} ${palette.ring}`
          }`}
          aria-hidden="true"
        >
          {isDefeated ? <Skull className="w-7 h-7" strokeWidth={2.4} /> : <Icon className="w-7 h-7" strokeWidth={2.4} />}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3
                className={`text-base sm:text-lg font-extrabold leading-tight truncate ${
                  isDefeated ? "text-slate-500 line-through decoration-2 decoration-slate-400/70" : "text-slate-900"
                }`}
                style={{ fontFamily: "Outfit, sans-serif" }}
                data-testid={`battle-card-name-${sub.id}`}
              >
                {sub.name}
              </h3>
              <span
                className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                  isDefeated ? "bg-slate-200 text-slate-600" : palette.chip
                }`}
              >
                {isDefeated ? "Slayed" : "Monthly drain"}
              </span>
            </div>
            <div className="flex items-start gap-1">
              <div className="text-right">
                <div
                  className="text-xl sm:text-2xl font-black text-slate-900 leading-none"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                  data-testid={`battle-card-cost-${sub.id}`}
                >
                  ${sub.monthly_cost.toFixed(2)}
                </div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mt-1">/month</div>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button" variant="ghost" size="sm"
                    className="h-8 w-8 p-0 -mr-1 -mt-1 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    aria-label="Battle options"
                    data-testid={`battle-card-menu-${sub.id}`}
                  >
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-xl">
                  <DropdownMenuItem
                    onSelect={() => setEditing(true)}
                    className="cursor-pointer font-semibold"
                    data-testid={`battle-card-edit-${sub.id}`}
                  >
                    <Pencil className="w-4 h-4" /> Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={handleDelete}
                    className="cursor-pointer font-semibold text-red-600 focus:text-red-700"
                    data-testid={`battle-card-delete-${sub.id}`}
                  >
                    <Trash2 className="w-4 h-4" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between text-xs font-semibold text-slate-500 mb-1.5">
              <span className="uppercase tracking-wider">{isDefeated ? "Reclaimed" : "Next charge"}</span>
              <span className={isDefeated ? "text-slate-500" : (urgent ? "text-red-600" : "text-slate-700")}>
                {isDefeated ? "$" + (sub.monthly_cost || 0).toFixed(2) + "/mo back" : `${remaining} ${remaining === 1 ? "day" : "days"} left`}
              </span>
            </div>
            <Progress
              value={isDefeated ? 100 : progressPct}
              className={`h-2 bg-slate-100 ${isDefeated ? "[&>div]:bg-slate-400" : (urgent ? "[&>div]:bg-red-500" : "[&>div]:bg-slate-900")}`}
              data-testid={`battle-card-progress-${sub.id}`}
            />
          </div>

          {isDefeated ? (
            <Button
              onClick={handleRestore}
              disabled={restoring}
              variant="ghost"
              className="mt-4 w-full h-11 rounded-2xl border-2 border-slate-300 bg-white text-slate-600 hover:bg-slate-50 hover:border-slate-400 font-bold text-sm tracking-wide transition-all duration-200"
              data-testid={`restore-bill-button-${sub.id}`}
            >
              <RotateCcw className="w-4 h-4 mr-2" strokeWidth={2.6} /> Restore battle
            </Button>
          ) : (
            <Button
              onClick={handleDefeat}
              disabled={defeating}
              className="mt-4 w-full h-11 rounded-2xl bg-red-500 hover:bg-red-600 active:scale-[0.98] text-white font-bold text-sm tracking-wide shadow-sm hover:shadow-md transition-all duration-200 focus:ring-4 focus:ring-red-500/30"
              data-testid={`defeat-bill-button-${sub.id}`}
            >
              {defeating ? (<><CheckCircle2 className="w-4 h-4 mr-2" /> Defeated!</>) : (<><Sword className="w-4 h-4 mr-2" strokeWidth={2.6} /> Defeat Bill</>)}
            </Button>
          )}
        </div>
      </div>

      <AddSubscriptionDialog
        editing={sub}
        open={editing}
        onOpenChange={setEditing}
        onSaved={(updated) => onChanged?.(updated)}
      />
    </div>
  );
};

export default BattleCard;
